/**
 * Invoice Request — Cart drawer accordion per dati di fatturazione italiana (solo Azienda).
 *
 * Comportamento:
 * - L'accordion fa da toggle: aperto = richiesta fattura, chiuso = no.
 * - Campi: Ragione sociale + P.IVA + Codice Fiscale azienda (facoltativo) + (PEC OR Codice SDI obbligatorio).
 * - Persistence: i dati restano salvati come cart attributes anche dopo navigazione
 *   (carrello → checkout → indietro) finché l'utente non chiude l'accordion.
 * - Alla chiusura esplicita dell'accordion → reset TOTALE: input UI svuotati,
 *   TUTTI gli attributi server-side cancellati.
 * - Multi-istanza: se il block è renderizzato sia nel cart drawer overlay che nella
 *   pagina /cart (via <cart-details>), tutte le istanze restano sincronizzate.
 * - Validazione bloccante SOLO al click checkout (nessun feedback live durante la digitazione).
 * - P.IVA accetta sia "12345678901" che "IT12345678901" (prefisso IT opzionale, case-insensitive).
 *
 * Re-init:
 * - subscribe(PUB_SUB_EVENTS.cartUpdate) per refresh con source = "product-form"
 * - MutationObserver su <cart-drawer> per refresh con source = "cart-items"
 * - Listener delegati su document con capture per checkout/submit (sopravvivono al re-render)
 *
 * Debug: window.__invoiceRequest
 */
(function () {
  'use strict';

  const SELECTORS = {
    root: '[data-invoice-request]',
    input: '[data-invoice-field]',
    fieldWrap: (k) => `[data-invoice-field-wrap="${k}"]`,
    error: (k) => `[data-invoice-error="${k}"]`,
    generalError: '[data-invoice-general-error]',
    cartDrawer: 'cart-drawer',
    checkoutBtns: [
      'button[name="checkout"]',
      '.cart__checkout-button',
      '.cart-drawer__checkout',
      'a[href="/checkout"]',
      'a[href^="/checkout"]'
    ],
    cartForm: '#CartDrawer-Form, #MainCartForm'
  };

  // Helper: trova il <details> ancestor di un root.
  const detailsOf = (root) => root.closest('details[' + 'data-invoice-details' + ']');

  // Ordine = ordine visivo dei campi (Codice Fiscale subito dopo la P.IVA).
  // Usato anche per l'ordine di focus sul primo campo in errore.
  const ATTR_KEYS = {
    'ragione-sociale': 'Ragione sociale',
    'partita-iva': 'Partita IVA',
    'codice-fiscale': 'Codice Fiscale',
    'pec': 'PEC',
    'sdi': 'Codice SDI'
  };

  const ERROR_MSG = {
    'ragione-sociale': 'Inserisci la ragione sociale o nome e cognome.',
    'partita-iva': 'La Partita IVA deve essere 11 cifre (es. 12345678901 o IT12345678901).',
    'pec': 'Inserisci un indirizzo PEC valido.',
    'sdi': 'Il codice SDI deve essere 6 o 7 caratteri alfanumerici.',
    'pec-or-sdi': 'Inserisci almeno una PEC valida o un Codice SDI.',
    'codice-fiscale': 'Il Codice Fiscale deve essere di 9, 11 o 16 caratteri.'
  };

  // ---- Validators ----
  const isNonEmpty = (s) => typeof s === 'string' && s.trim().length > 0;
  // P.IVA italiana: prefisso "IT" opzionale (case-insensitive) seguito da esattamente 11 cifre.
  const isValidVAT = (s) => /^(IT)?[0-9]{11}$/i.test((s || '').trim().replace(/\s+/g, ''));
  const isValidEmail = (s) => /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/.test((s || '').trim());
  // Codice Fiscale (facoltativo): se valorizzato deve avere 9, 11 o 16 caratteri (spazi ignorati).
  const isValidCF = (s) => {
    const v = (s || '').trim().replace(/\s+/g, '');
    return v.length === 9 || v.length === 11 || v.length === 16;
  };
  // ---- Utils ----
  function debounce(fn, ms) {
    let t;
    return function (...args) {
      clearTimeout(t);
      t = setTimeout(() => fn.apply(this, args), ms);
    };
  }

  function getCartUpdateUrl() {
    const base = (window.routes && window.routes.cart_update_url) || '/cart/update';
    return base + '.js';
  }

  // ---- Controller ----
  class InvoiceRequestController {
    constructor() {
      this.abortController = null;
      this.pendingPostCtrl = null;
      this.mo = null;
      this.currentRoots = [];
      this.subscribed = false;
      this.pubsubRetries = 0;
    }

    getRoots() {
      return Array.from(document.querySelectorAll(SELECTORS.root));
    }

    boot() {
      this.init();
      this.subscribeToPubSub();
      this.attachMutationObserver();
    }

    resetUI(root) {
      root.querySelectorAll(SELECTORS.input).forEach((i) => {
        i.value = '';
      });
      this.clearAllErrors(root);
    }

    subscribeToPubSub() {
      if (this.subscribed) return;
      if (window.subscribe && window.PUB_SUB_EVENTS && window.PUB_SUB_EVENTS.cartUpdate) {
        window.subscribe(window.PUB_SUB_EVENTS.cartUpdate, () => this.init());
        this.subscribed = true;
      } else if (this.pubsubRetries < 10) {
        this.pubsubRetries += 1;
        setTimeout(() => this.subscribeToPubSub(), 50);
      }
    }

    attachMutationObserver() {
      if (this.mo) return;
      // Osserviamo SEMPRE il body: i roots possono comparire/sparire sia nel
      // <cart-drawer> overlay sia in <cart-details> (pagina /cart).
      const handler = debounce(() => {
        const newRoots = this.getRoots();
        const sameSet =
          newRoots.length === this.currentRoots.length &&
          newRoots.every((r) => this.currentRoots.includes(r));
        if (!sameSet) this.init();
      }, 100);
      this.mo = new MutationObserver(handler);
      this.mo.observe(document.body, { childList: true, subtree: true });
    }

    init() {
      this.cleanup();
      const roots = this.getRoots();
      if (!roots.length) {
        this.currentRoots = [];
        return;
      }
      this.currentRoots = roots;
      this.abortController = new AbortController();
      const signal = this.abortController.signal;

      // Ripopoliamo SEMPRE da cart attributes. I dati sopravvivono alla
      // navigazione (carrello → checkout → indietro) finché l'utente non
      // chiude esplicitamente l'accordion (= "non voglio fattura").
      roots.forEach((root) => this.restoreState(root));

      roots.forEach((root) => this.bindRoot(root, signal));

      // Listener delegati su document (uno solo, sopravvive al re-render).
      document.addEventListener('click', (e) => this.onCheckoutClick(e), { signal, capture: true });
      document.addEventListener('submit', (e) => this.onFormSubmit(e), { signal, capture: true });
    }

    bindRoot(root, signal) {
      // Input → POST debounced + clear error
      root.querySelectorAll(SELECTORS.input).forEach((input) => {
        const key = input.getAttribute('data-invoice-field');
        const debounced = debounce(() => this.onFieldInput(key, input), 400);
        input.addEventListener('input', debounced, { signal });
        input.addEventListener(
          'input',
          () => {
            this.clearError(root, key);
            if (key === 'pec' || key === 'sdi') this.clearError(root, 'pec-or-sdi');
            // Sync UI fra istanze diverse dello stesso block
            this.syncInputAcrossRoots(root, key, input.value);
          },
          { signal }
        );
      });

      // <details> toggle
      const details = detailsOf(root);
      if (details) {
        details.addEventListener('toggle', () => this.onDetailsToggle(details), { signal });
      }
    }

    syncInputAcrossRoots(sourceRoot, key, value) {
      // Replica il valore digitato in tutti gli altri roots (drawer ↔ pagina /cart).
      this.currentRoots.forEach((r) => {
        if (r === sourceRoot) return;
        const input = r.querySelector(`[data-invoice-field="${key}"]`);
        if (input && input.value !== value) input.value = value;
      });
    }

    cleanup() {
      if (this.abortController) {
        this.abortController.abort();
        this.abortController = null;
      }
    }

    restoreState(root) {
      // Accordion chiuso → svuota gli input (evita race con re-render del drawer
      // dopo clearAllAttributes, in cui Liquid potrebbe aver ripopolato i value).
      const details = detailsOf(root);
      if (details && !details.open) {
        root.querySelectorAll(SELECTORS.input).forEach((i) => {
          i.value = '';
        });
        return;
      }

      const attrs =
        (window.Debutify && window.Debutify.cart && window.Debutify.cart.attributes) || null;
      if (!attrs) return; // Liquid ha già pre-popolato server-side.

      Object.entries(ATTR_KEYS).forEach(([key, attrKey]) => {
        const value = attrs[attrKey] || '';
        const input = root.querySelector(`[data-invoice-field="${key}"]`);
        if (input && input.value !== value) {
          input.value = value;
        }
      });
    }

    onDetailsToggle(details) {
      if (details.open) return; // L'apertura non triggera azioni automatiche.

      // Chiusura accordion → reset TOTALE su TUTTI i roots (UI + server),
      // anche se compilati. Lo stato server è uno solo: applichiamolo a tutti.
      if (this.pendingPostCtrl) {
        this.pendingPostCtrl.abort();
        this.pendingPostCtrl = null;
      }
      this.currentRoots.forEach((r) => {
        this.resetUI(r);
        const d = detailsOf(r);
        if (d && d !== details && d.open) d.removeAttribute('open');
      });
      this.clearAllAttributes();
    }

    onFieldInput(key, input) {
      // POST silenzioso dell'attributo. Nessuna validazione live.
      this.postAttribute(key, input.value);
    }

    async postAttribute(key, value) {
      const attrKey = ATTR_KEYS[key];
      if (!attrKey) return;
      this.syncLocalAttrs({ [attrKey]: value });
      try {
        if (this.pendingPostCtrl) this.pendingPostCtrl.abort();
        this.pendingPostCtrl = new AbortController();
        await fetch(getCartUpdateUrl(), {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Accept': 'application/json'
          },
          body: JSON.stringify({ attributes: { [attrKey]: value } }),
          signal: this.pendingPostCtrl.signal
        });
      } catch (err) {
        if (err && err.name !== 'AbortError') {
          console.warn('[invoice-request] postAttribute failed:', err);
        }
      }
    }

    syncLocalAttrs(attributes) {
      // Sincronizza window.Debutify.cart.attributes con il payload appena postato.
      // Evita race: se il drawer si re-renderizza prima della response, restoreState
      // legge i valori già aggiornati invece di quelli vecchi.
      if (!window.Debutify || !window.Debutify.cart) return;
      if (!window.Debutify.cart.attributes) window.Debutify.cart.attributes = {};
      Object.assign(window.Debutify.cart.attributes, attributes);
    }

    async postAttributes(payload) {
      this.syncLocalAttrs(payload.attributes || {});
      try {
        await fetch(getCartUpdateUrl(), {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Accept': 'application/json'
          },
          body: JSON.stringify(payload)
        });
      } catch (err) {
        console.warn('[invoice-request] postAttributes failed:', err);
      }
    }

    async clearAllAttributes() {
      const payload = { attributes: {} };
      Object.values(ATTR_KEYS).forEach((k) => {
        payload.attributes[k] = '';
      });
      await this.postAttributes(payload);
    }

    /**
     * Validazione globale: scatta solo se l'accordion è aperto (= utente richiede fattura).
     * Se chiuso, ritorna sempre valid.
     * Regole: Ragione sociale + P.IVA obbligatori, almeno una fra PEC e Codice SDI.
     */
    validateAll(root) {
      const details = detailsOf(root);
      if (!details || !details.open) return { valid: true, errors: {} };

      const errors = {};
      const valueOf = (key) => {
        const i = root.querySelector(`[data-invoice-field="${key}"]`);
        return i ? i.value : '';
      };

      const rs = valueOf('ragione-sociale');
      const piva = valueOf('partita-iva');
      const pec = valueOf('pec');
      const sdi = valueOf('sdi');
      const cf = valueOf('codice-fiscale');

      if (!isNonEmpty(rs)) errors['ragione-sociale'] = ERROR_MSG['ragione-sociale'];
      if (!isValidVAT(piva)) errors['partita-iva'] = ERROR_MSG['partita-iva'];

      const pecFilled = isNonEmpty(pec);
      const sdiFilled = isNonEmpty(sdi);

      if (!pecFilled && !sdiFilled) {
        errors['pec-or-sdi'] = ERROR_MSG['pec-or-sdi'];
      } else {
        if (pecFilled && !isValidEmail(pec)) errors['pec'] = ERROR_MSG['pec'];
        if (sdiFilled && !/^[A-Za-z0-9]{6,7}$/.test(sdi.trim())) {
          errors['sdi'] = ERROR_MSG['sdi'];
        }
      }

      // Codice Fiscale: facoltativo, ma se compilato deve essere di 9, 11 o 16 caratteri.
      if (isNonEmpty(cf) && !isValidCF(cf)) {
        errors['codice-fiscale'] = ERROR_MSG['codice-fiscale'];
      }

      return { valid: Object.keys(errors).length === 0, errors };
    }

    errorWraps(root, key) {
      // pec-or-sdi colpisce entrambi i wrap PEC e SDI
      if (key === 'pec-or-sdi') {
        return [
          root.querySelector(SELECTORS.fieldWrap('pec')),
          root.querySelector(SELECTORS.fieldWrap('sdi'))
        ].filter(Boolean);
      }
      const w = root.querySelector(SELECTORS.fieldWrap(key));
      return w ? [w] : [];
    }

    showError(root, key, msg) {
      const slot = root.querySelector(SELECTORS.error(key));
      if (slot) {
        slot.textContent = msg;
        slot.removeAttribute('hidden');
      }
      this.errorWraps(root, key).forEach((w) => w.classList.add('is-error'));
    }

    clearError(root, key) {
      const slot = root.querySelector(SELECTORS.error(key));
      if (slot) {
        slot.textContent = '';
        slot.setAttribute('hidden', '');
      }
      this.errorWraps(root, key).forEach((w) => w.classList.remove('is-error'));
    }

    clearAllErrors(root) {
      Object.keys(ATTR_KEYS).forEach((k) => this.clearError(root, k));
      this.clearError(root, 'pec-or-sdi');
      this.clearGeneralError(root);
    }

    showGeneralError(root) {
      const slot = root.querySelector(SELECTORS.generalError);
      if (slot) slot.removeAttribute('hidden');
    }

    clearGeneralError(root) {
      const slot = root.querySelector(SELECTORS.generalError);
      if (slot) slot.setAttribute('hidden', '');
    }

    isCheckoutTarget(target) {
      if (!target || !target.closest) return false;
      for (const sel of SELECTORS.checkoutBtns) {
        if (target.closest(sel)) return true;
      }
      return false;
    }

    rootForContext(target) {
      // Cerca il root nel contesto del button/form cliccato.
      // 1) Stesso form ancestor (preferenza).
      // 2) Stesso ancestor <cart-drawer> o <cart-details> o sezione cart.
      // 3) Fallback: primo root con details.open.
      if (target && target.closest) {
        const form = target.closest('form');
        if (form) {
          const r = form.querySelector(SELECTORS.root);
          if (r) return r;
        }
        const container = target.closest('cart-drawer, cart-details, .cart-block, #shopify-section-cart-details');
        if (container) {
          const r = container.querySelector(SELECTORS.root);
          if (r) return r;
        }
      }
      return this.currentRoots.find((r) => {
        const d = detailsOf(r);
        return d && d.open;
      }) || this.currentRoots[0] || null;
    }

    onCheckoutClick(e) {
      if (!this.isCheckoutTarget(e.target)) return;
      const root = this.rootForContext(e.target);
      if (!root) return;
      const details = detailsOf(root);
      if (!details || !details.open) return; // accordion chiuso → pass-through
      const result = this.validateAll(root);
      if (result.valid) return;
      e.preventDefault();
      e.stopPropagation();
      if (typeof e.stopImmediatePropagation === 'function') e.stopImmediatePropagation();
      this.blockAndShowErrors(root, result.errors);
    }

    onFormSubmit(e) {
      const form = e.target;
      if (!form || !form.matches) return;
      if (!form.matches(SELECTORS.cartForm)) return;
      const root = form.querySelector(SELECTORS.root) || this.rootForContext(form);
      if (!root) return;
      const details = detailsOf(root);
      if (!details || !details.open) return;
      const result = this.validateAll(root);
      if (result.valid) return;
      e.preventDefault();
      e.stopPropagation();
      if (typeof e.stopImmediatePropagation === 'function') e.stopImmediatePropagation();
      this.blockAndShowErrors(root, result.errors);
    }

    blockAndShowErrors(root, errors) {
      const details = detailsOf(root);
      if (details && !details.hasAttribute('open')) details.setAttribute('open', '');
      Object.entries(errors).forEach(([key, msg]) => this.showError(root, key, msg));
      this.showGeneralError(root);
      try {
        root.scrollIntoView({ behavior: 'smooth', block: 'center' });
      } catch (_) {
        /* noop */
      }
      // Focus sul primo campo in errore.
      const orderedKeys = Object.keys(ATTR_KEYS);
      const errorKey =
        orderedKeys.find((k) => errors[k]) ||
        (errors['pec-or-sdi'] ? 'pec' : null);
      if (errorKey) {
        const firstInput = root.querySelector(`[data-invoice-field="${errorKey}"]`);
        if (firstInput) firstInput.focus();
      }
    }
  }

  const controller = new InvoiceRequestController();
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => controller.boot());
  } else {
    controller.boot();
  }
  // Esposto solo per debug da console.
  window.__invoiceRequest = controller;
})();
